"use client";

import Image from "next/image";
import { useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { useAuth } from "@trex-2b/hooks";
import { buildTrackerProps } from "@trex-2b/libs/tracker";
import { LOGIN_STRATEGY_STORAGE_KEY } from "@trex-2b/libs/config";
import type { StepLoginMethod } from "@trex-2b/libs/config/login-types";
import { Modal } from "../modal";

export type { StepLoginMethod } from "@trex-2b/libs/config/login-types";

type ModalAuthLoginProps = {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
};

export type ShowModalAuthLoginOptions = {
  onLoginSuccess?: () => void;
};

const OAUTH_BUTTONS: ReadonlyArray<{
  method: StepLoginMethod;
  label: string;
  iconPath: string;
}> = [
  {
    method: "google",
    label: "google",
    iconPath: "/images/portal-login/google-with-colour.svg",
  },
  {
    method: "x",
    label: "x",
    iconPath: "/images/portal-login/x-logo-black.svg",
  },
  {
    method: "apple",
    label: "apple",
    iconPath: "/images/portal-login/login_apple.svg",
  },
  {
    method: "discord",
    label: "discord",
    iconPath: "/images/portal-login/login_discord.svg",
  },
  {
    method: "telegram",
    label: "telegram",
    iconPath: "/images/portal-login/login_telegram.svg",
  },
  {
    method: "facebook",
    label: "facebook",
    iconPath: "/images/portal-login/login_facebook.svg",
  },
];

function LoginOptionButton({
  label,
  iconPath,
  isLastLogin,
  disabled,
  onClick,
  testId,
}: {
  label: string;
  iconPath: string;
  isLastLogin?: boolean;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <div className="relative overflow-visible">
      {isLastLogin ? (
        <div className="pointer-events-none absolute top-[-15px] right-0 z-1 flex h-[38px] w-[82px] justify-center">
          <Image
            src="/images/portal-login/lastlogin_icon.png"
            alt=""
            fill
            sizes="82px"
            className="object-cover object-center"
          />
          <span className="relative z-10 mt-[2px] text-[12px] font-medium leading-[12px] text-v2-content-inverse_primary">
            Last login
          </span>
        </div>
      ) : null}
      <button
        type="button"
        data-testid={testId}
        {...buildTrackerProps({ params: { element_id: testId } })}
        disabled={disabled}
        onClick={onClick}
        className="relative z-10 flex h-[48px] w-full items-center justify-center gap-1 rounded-lg border border-[rgba(28,25,22,0.1)] bg-white px-3 py-[14px] cursor-pointer"
      >
        <Image
          src={iconPath}
          alt={label}
          width={20}
          height={20}
          className="size-5"
        />
        <span className="font-poppins text-[14px] font-semibold leading-[18px] text-[rgba(28,25,22,0.8)] first-letter:uppercase">
          {label}
        </span>
      </button>
    </div>
  );
}

export function ModalAuthLogin({
  isOpen,
  onClose,
  onLoginSuccess,
}: ModalAuthLoginProps) {
  const [lastLoginMethod, setLastLoginMethod] =
    useState<StepLoginMethod | null>(() =>
      typeof window === "undefined"
        ? null
        : (window.localStorage.getItem(
            LOGIN_STRATEGY_STORAGE_KEY,
          ) as StepLoginMethod | null),
    );
  /** 点击后立即为 true，避免仅靠 hook 时首帧未刷出转圈 */
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { login, isLoading } = useAuth();
  const showLoadingSpinner = isLoading || isAuthenticating;

  const handleLoginClick = async (method: StepLoginMethod) => {
    flushSync(() => {
      setIsAuthenticating(true);
    });
    try {
      await login(method, () => {
        setLastLoginMethod(method);
        onLoginSuccess?.();
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="auth-login-modal"
      className="h-auto w-[461px] rounded-[8px] border border-[rgba(28,25,22,0.4)] bg-white px-6 pb-6 pt-[46px] min-h-0"
    >
      <div className="relative flex flex-col gap-6 overflow-visible">
        <div className="flex flex-col gap-3">
          <h2 className="text-[24px] font-semibold leading-5 tracking-[0.14px] text-black">
            Sign in to continue
          </h2>
          <p className="text-[16px] font-normal leading-[30.8px] tracking-[-0.3px] text-[rgba(28,25,22,0.6)]">
            Sign in to launch a growth campaign for your project.
          </p>
        </div>

        <div className="grid w-full grid-cols-2 gap-6 overflow-visible">
          {OAUTH_BUTTONS.map((oauth) => (
            <LoginOptionButton
              key={oauth.method}
              testId={`auth-login-${oauth.method}-btn`}
              label={oauth.label}
              iconPath={oauth.iconPath}
              isLastLogin={
                !!lastLoginMethod && lastLoginMethod === oauth.method
              }
              disabled={showLoadingSpinner}
              onClick={() => void handleLoginClick(oauth.method)}
            />
          ))}
        </div>

        <div className="w-full">
          <LoginOptionButton
            testId="auth-login-line-btn"
            label="line"
            iconPath="/images/portal-login/login_line.svg"
            isLastLogin={!!lastLoginMethod && lastLoginMethod === "line"}
            disabled={showLoadingSpinner}
            onClick={() => void handleLoginClick("line")}
          />
        </div>

        {showLoadingSpinner ? (
          <div
            className="pointer-events-none absolute inset-0 z-100 flex items-center justify-center rounded-[8px]"
            aria-busy
            aria-live="polite"
          >
            <span
              className="size-10 inline-block shrink-0 animate-spin rounded-full border-4 border-[rgba(28,25,22,0.2)] border-t-[#1c1916]"
              aria-hidden
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export function showModalAuthLogin(options?: ShowModalAuthLoginOptions) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const cleanup = () => {
    root.unmount();
    container.remove();
  };

  root.render(
    <ModalAuthLogin
      isOpen
      onClose={cleanup}
      onLoginSuccess={() => {
        options?.onLoginSuccess?.();
        cleanup();
      }}
    />,
  );
}
